import * as React from 'react';
import type {Placement, PlacementAxis} from '@react-types/overlays';
import {useId, useFocusVisible, mergeRefs} from '@sail/react-aria';
import * as Theme from '@sail/theme-classic';
import {AnimatePresence} from '@sail/engine';
import {animate} from '@sail/engine/deprecated';

import {Layer} from './Layer';
import {useLayerPosition} from '../../../components/layers/useLayerPosition';
import {view} from '../../view';

const ANIMATION_DURATION = 150;
const COOLDOWN_DURATION = 500;
const MINIMUM_DURATION = 800;

export interface TooltipProps {
  /**
   * The target element that should be used as the Tooltip trigger.
   * @external
   */
  trigger: React.ReactElement | React.RefObject<HTMLElement>;

  /**
   * How the Tooltip should be placed relative to the trigger element
   * @external
   */
  placement?: Placement;

  /**
   * How much time in milliseconds to wait before showing the Tooltip on hover
   * @external
   */
  delay?: number;

  /**
   * The style variant of the Tooltip
   * @external
   */
  type?: 'description' | 'label';
}

function getTransformAnimation(placement?: PlacementAxis) {
  const TRANSLATE_DISTANCE = '4px';
  if (placement?.startsWith('bottom')) {
    return `translate3d(0, -${TRANSLATE_DISTANCE}, 0)`;
  } else if (placement?.startsWith('top')) {
    return `translate3d(0, ${TRANSLATE_DISTANCE}, 0)`;
  } else if (placement?.startsWith('left')) {
    return `translate3d(${TRANSLATE_DISTANCE}, 0, 0)`;
  } else if (placement?.startsWith('right')) {
    return `translate3d(-${TRANSLATE_DISTANCE}, 0, 0)`;
  } else {
    return '';
  }
}

type TooltipStateControl = (options?: {immediate?: boolean}) => void;

type TooltipCommand = 'open' | 'close';
type TooltipInterval = 'warmup' | 'cooldown' | 'minimum';
type PendingAction = {
  command: TooltipCommand;
  after: TooltipInterval;
};
type TooltipAction = TooltipCommand | PendingAction;

type TooltipStatus = 'open' | 'closed';

type TooltipState = {
  status: TooltipStatus;
  pending: PendingAction | null;
  openedAt: number | null;
};

function tooltipStateReducer(
  state: TooltipState,
  action: TooltipAction,
): TooltipState {
  switch (action) {
    case 'open':
      return {
        status: 'open',
        pending: null,
        openedAt: Date.now(),
      };
    case 'close':
      return {
        ...state,
        status: 'closed',
        pending: null,
        openedAt: null,
      };
    default: {
      return {
        ...state,
        pending: action,
      };
    }
  }
}

let activeTooltipDispatch: React.Dispatch<TooltipAction> | null = null;

function useTooltipState({delay}: {delay: number}) {
  const {isFocusVisible} = useFocusVisible();
  const [state, dispatch] = React.useReducer(tooltipStateReducer, {
    status: 'closed',
    pending: null,
    openedAt: null,
  });

  const openTimeout = React.useRef<number>();
  const closeTimeout = React.useRef<number>();

  const open = React.useCallback<TooltipStateControl>(
    ({immediate = false} = {}) => {
      dispatch(
        immediate || delay <= 0 || activeTooltipDispatch !== null
          ? 'open'
          : {command: 'open', after: 'warmup'},
      );
    },
    [delay],
  );

  const close = React.useCallback<TooltipStateControl>(
    ({immediate = false} = {}) => {
      if (isFocusVisible) {
        return dispatch('close');
      }

      dispatch({
        command: 'close',
        after: immediate ? 'minimum' : 'cooldown',
      });
    },
    [isFocusVisible],
  );

  React.useEffect(() => {
    if (state.status === 'open' && activeTooltipDispatch !== dispatch) {
      activeTooltipDispatch?.('close');
      activeTooltipDispatch = dispatch;
    }

    if (state.status === 'closed' && activeTooltipDispatch === dispatch) {
      activeTooltipDispatch = null;
    }

    if (state.pending?.command === 'open') {
      const timeout = window.setTimeout(() => {
        dispatch('open');
      }, delay);
      openTimeout.current = timeout;
      return () => {
        window.clearTimeout(timeout);
      };
    }

    if (state.pending?.command === 'close') {
      const after = state.pending.after;

      const uptime = state.openedAt ? Date.now() - state.openedAt : 0;
      const closeAt =
        after === 'minimum' ? MINIMUM_DURATION - uptime : COOLDOWN_DURATION;

      if (closeAt <= 0) {
        dispatch('close');
      } else {
        const timeout = window.setTimeout(() => {
          dispatch('close');
        }, closeAt);
        openTimeout.current = timeout;
        return () => {
          window.clearTimeout(timeout);
        };
      }
    }
  }, [openTimeout, closeTimeout, delay, state, isFocusVisible]);

  return {
    isOpen: state.status === 'open',
    open,
    close,
  };
}

type TooltipLayerProps = {
  placement: TooltipProps['placement'];
  type: TooltipProps['type'];
  overlayRef: React.RefObject<HTMLDivElement>;
  triggerRef: React.RefObject<HTMLElement>;
};

const TooltipLayer = view.div.create<TooltipLayerProps>(
  ({placement, type, overlayRef, triggerRef, children, ...props}, View) => {
    const {
      props: layerProps,
      state: layerState,
      container,
    } = useLayerPosition({
      placement,
      targetRef: triggerRef,
      overlayRef,
      shouldRespectScrollBoundary: false,
      onClose: () => undefined,
    });

    const computedPlacement = layerState.position?.placement;

    return (
      <Layer
        type="partial"
        uses={[layerProps, {ref: overlayRef}]}
        container={container}
      >
        <View
          uses={[
            props,
            Theme.Tooltip,
            Theme.TooltipVariants.assign({
              type,
              placement: computedPlacement,
            }),
            {role: 'tooltip'},
            animate.inout({duration: ANIMATION_DURATION, opacity: 0}),
            animate.inout({
              duration: ANIMATION_DURATION,
              transform: getTransformAnimation(computedPlacement),
            }),
          ]}
        >
          {children}
        </View>
      </Layer>
    );
  },
);

/**
 * @external
 */
export const Tooltip = view.div.create<TooltipProps>(
  ({
    trigger,
    placement = 'bottom',
    delay,
    type = 'description',
    children,
    ...props
  }) => {
    if (
      React.isValidElement(trigger) &&
      (trigger.type as unknown as symbol) === Symbol.for('react.fragment') &&
      process.env.NODE_ENV !== 'production'
    ) {
      throw new Error(
        'React fragments are not supported as Sail Tooltip triggers.',
      );
    }

    const {isFocusVisible} = useFocusVisible();
    const triggerRef = React.useRef<HTMLElement | null>(null);
    const overlayRef = React.useRef<HTMLDivElement>(null);

    if (delay === undefined) {
      // eslint-disable-next-line no-param-reassign
      delay = 250;
    }

    React.useEffect(() => {
      if ('current' in trigger) {
        triggerRef.current = trigger.current;
      }
    });

    const {
      isOpen,
      open: showTooltip,
      close: hideTooltip,
    } = useTooltipState({delay});

    const id = useId();

    React.useEffect(() => {
      const triggerElement = triggerRef.current;
      const overlayElement = overlayRef.current;

      function onFocus() {
        if (isFocusVisible) {
          showTooltip({immediate: true});
        }
      }

      function onBlur() {
        hideTooltip({immediate: true});
      }

      function onMouseEnter() {
        showTooltip();
      }

      function onMouseLeave() {
        hideTooltip();
      }

      if (triggerElement) {
        triggerElement.addEventListener('focus', onFocus);
        triggerElement.addEventListener('blur', onBlur);
        triggerElement.addEventListener('mouseenter', onMouseEnter);
        triggerElement.addEventListener('mouseleave', onMouseLeave);
      }

      if (overlayElement && type === 'description') {
        overlayElement.addEventListener('focus', onFocus);
        overlayElement.addEventListener('blur', onBlur);
        overlayElement.addEventListener('mouseenter', onMouseEnter);
        overlayElement.addEventListener('mouseleave', onMouseLeave);
      }

      if (isOpen) {
        document.body.addEventListener('scroll', onBlur, {capture: true});
      }

      return () => {
        if (triggerElement) {
          triggerElement.removeEventListener('focus', onFocus);
          triggerElement.removeEventListener('blur', onBlur);
          triggerElement.removeEventListener('mouseenter', onMouseEnter);
          triggerElement.removeEventListener('mouseleave', onMouseLeave);
        }

        if (overlayElement && type === 'description') {
          overlayElement.removeEventListener('focus', onFocus);
          overlayElement.removeEventListener('blur', onBlur);
          overlayElement.removeEventListener('mouseenter', onMouseEnter);
          overlayElement.removeEventListener('mouseleave', onMouseLeave);
        }

        if (isOpen) {
          document.body.removeEventListener('scroll', onBlur, {
            capture: true,
          });
        }
      };
    }, [
      triggerRef,
      overlayRef,
      showTooltip,
      hideTooltip,
      isOpen,
      type,
      isFocusVisible,
    ]);

    React.useEffect(() => {
      function onKeyDown(event: KeyboardEvent) {
        if (event.key === 'Escape') {
          event.preventDefault();
          hideTooltip({immediate: true});
        }
      }

      if (isOpen) {
        window.addEventListener('keydown', onKeyDown);

        return () => {
          window.removeEventListener('keydown', onKeyDown);
        };
      }
    }, [isOpen, hideTooltip]);

    React.useEffect(() => {
      const triggerElement = triggerRef.current;

      triggerElement?.setAttribute('tabindex', '0');

      if (isOpen) {
        const attribute =
          type === 'description' ? 'aria-describedby' : 'aria-labelledby';

        triggerElement?.setAttribute(attribute, id);

        return () => {
          triggerElement?.removeAttribute(attribute);
        };
      }
    }, [isOpen, id, type]);

    return (
      <>
        {React.isValidElement<{
          ref: React.ForwardedRef<HTMLElement | null> | undefined;
        }>(trigger) &&
          React.cloneElement(trigger, {
            ref: mergeRefs<HTMLElement | null>(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (trigger as any).ref,
              triggerRef,
            ),
          })}

        <AnimatePresence>
          {isOpen ? (
            <TooltipLayer
              id={id}
              type={type}
              placement={placement}
              overlayRef={overlayRef}
              triggerRef={triggerRef}
              uses={[props]}
            >
              {children}
            </TooltipLayer>
          ) : null}
        </AnimatePresence>
      </>
    );
  },
);
